import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import clsx from 'clsx';

import { useResolvedTheme } from '../theme';
import Modal, { Button, inputClass } from './Modal';
import { usePlanActions } from './data';
import type { Plan, Rhythm } from './types';
import { minutesLabel, timeRange, tint } from './util';

interface Props {
  plan: Plan;
  rhythms: Rhythm[];
  onClose: () => void;
}

function roundedNow(): string {
  const d = new Date();
  d.setMinutes(Math.floor(d.getMinutes() / 15) * 15, 0, 0);
  return format(d, 'HH:mm');
}

export default function LogModal({ plan, rhythms, onClose }: Props) {
  const theme = useResolvedTheme();
  const actions = usePlanActions();
  const days = plan.days.map((d) => d.date).filter((d) => d <= plan.today);
  const [day, setDay] = useState(days.includes(plan.today) ? plan.today : days[days.length - 1]);
  const [at, setAt] = useState(roundedNow());
  const [flash, setFlash] = useState<string | null>(null);

  const pd = plan.days.find((d) => d.date === day);
  const logged = pd?.blocks.filter((b) => b.type === 'done') ?? [];
  const fromCalendar = plan.events.filter(
    (e) => e.rhythm_id && !e.all_day && format(new Date(e.start), 'yyyy-MM-dd') === day && new Date(e.start) <= new Date()
  );
  const countByRhythm = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of logged) m.set(b.rhythm_id, (m.get(b.rhythm_id) ?? 0) + 1);
    for (const e of fromCalendar) m.set(e.rhythm_id!, (m.get(e.rhythm_id!) ?? 0) + 1);
    return m;
  }, [logged, fromCalendar]);

  function startAt(minutes: number): string {
    const [h, m] = at.split(':').map(Number);
    const d = parseISO(day);
    d.setHours(h, m, 0, 0);
    // "At" is when it ended; a session logged at 3:00 for an hour ran 2–3.
    d.setMinutes(d.getMinutes() - minutes);
    return d.toISOString();
  }

  function log(r: Rhythm, minutes: number) {
    actions.log.mutate(
      { rhythm_id: r.id, start: startAt(minutes), minutes },
      {
        onSuccess: () => {
          setFlash(r.id);
          window.setTimeout(() => setFlash((f) => (f === r.id ? null : f)), 700);
        }
      }
    );
  }

  const active = rhythms.filter((r) => r.active);
  const byId = new Map(rhythms.map((r) => [r.id, r]));
  const i = days.indexOf(day);

  return (
    <Modal
      title="Log"
      subtitle={
        <span className="flex items-center gap-2">
          <button
            type="button"
            disabled={i <= 0}
            onClick={() => setDay(days[i - 1])}
            className="grid h-6 w-6 place-items-center rounded hover:bg-[var(--bg-hover)] disabled:opacity-30"
            aria-label="previous day"
          >
            ‹
          </button>
          <span className="min-w-[110px] text-center">
            {day === plan.today ? 'Today' : format(parseISO(day), 'EEEE, MMM d')}
          </span>
          <button
            type="button"
            disabled={i >= days.length - 1}
            onClick={() => setDay(days[i + 1])}
            className="grid h-6 w-6 place-items-center rounded hover:bg-[var(--bg-hover)] disabled:opacity-30"
            aria-label="next day"
          >
            ›
          </button>
        </span>
      }
      onClose={onClose}
      width={600}
      footer={
        <>
          <label className="flex items-center gap-2 whitespace-nowrap text-[0.82rem] text-[var(--text-muted)]">
            Finished at
            <input type="time" step={900} value={at} onChange={(e) => setAt(e.target.value)} className={clsx(inputClass, 'w-[110px] py-[4px]')} />
          </label>
          <span className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        {active.map((r) => {
          const t = tint(r.color, theme);
          const n = countByRhythm.get(r.id) ?? 0;
          const hit = flash === r.id;
          if (r.kind === 'count') {
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => log(r, r.duration_minutes)}
                className={clsx(
                  'flex items-center gap-3 rounded-lg border px-3 py-[10px] text-left transition-[background,transform] active:scale-[0.98]',
                  hit && 'scale-[1.02]'
                )}
                style={{
                  background: n ? t.soft : 'var(--bg)',
                  borderColor: n ? t.line : 'var(--border)'
                }}
              >
                <span
                  className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full text-[0.8rem] font-semibold transition-colors"
                  style={n ? { background: t.solid, color: t.fg } : { border: `1.5px solid ${t.line}` }}
                >
                  {n ? (n > 1 ? n : '✓') : ''}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[0.88rem]">{r.name}</span>
                  <span className="block text-[0.72rem] text-[var(--text-muted)]">{minutesLabel(r.duration_minutes)}</span>
                </span>
              </button>
            );
          }
          return (
            <div
              key={r.id}
              className={clsx('flex flex-col gap-2 rounded-lg border px-3 py-[10px] transition-transform', hit && 'scale-[1.02]')}
              style={{ background: n ? t.softer : 'var(--bg)', borderColor: n ? t.line : 'var(--border)' }}
            >
              <span className="flex items-center gap-2">
                <span className="h-[10px] w-[10px] rounded-[3px]" style={{ background: t.solid }} />
                <span className="min-w-0 flex-1 truncate text-[0.88rem]">{r.name}</span>
              </span>
              <span className="flex gap-1">
                {[30, 60, 120].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => log(r, m)}
                    className="flex-1 rounded-md border border-[var(--border)] py-[3px] text-[0.76rem] text-[var(--text-muted)] hover:border-[var(--border-strong)] hover:text-[var(--text)]"
                  >
                    +{minutesLabel(m)}
                  </button>
                ))}
              </span>
            </div>
          );
        })}
      </div>

      {(logged.length > 0 || fromCalendar.length > 0) && (
        <div className="mt-5">
          <h3 className="mb-1 text-[0.72rem] font-medium uppercase tracking-wide text-[var(--text-faint)]">
            {day === plan.today ? 'Today' : format(parseISO(day), 'EEEE')}
          </h3>
          <div className="flex flex-col">
            {[...logged.map((b) => ({ kind: 'log' as const, id: b.id, rid: b.rhythm_id, s: b.start, e: b.end })),
              ...fromCalendar.map((e) => ({ kind: 'event' as const, id: e.id, rid: e.rhythm_id!, s: e.start, e: e.end, title: e.summary }))]
              .sort((a, b) => a.s.localeCompare(b.s))
              .map((row) => {
                const r = byId.get(row.rid);
                const t = tint(r?.color, theme);
                return (
                  <div key={row.id} className="group flex items-center gap-3 rounded-md px-2 py-[6px] hover:bg-[var(--bg-hover)]">
                    <span className="h-[10px] w-[10px] rounded-[3px]" style={{ background: t.solid }} />
                    <span className="min-w-0 flex-1 truncate text-[0.86rem]">
                      {r?.name}
                      {row.kind === 'event' && <span className="text-[var(--text-muted)]">  from “{'title' in row ? row.title : ''}”</span>}
                    </span>
                    <span className="text-[0.78rem] tabular-nums text-[var(--text-muted)]">{timeRange(new Date(row.s), new Date(row.e))}</span>
                    {row.kind === 'log' ? (
                      <button
                        type="button"
                        aria-label="undo"
                        onClick={() => actions.unlog.mutate(row.id)}
                        className="px-1 text-[var(--text-faint)] opacity-0 hover:text-[var(--text)] group-hover:opacity-100"
                      >
                        ×
                      </button>
                    ) : (
                      <span className="w-[18px]" />
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </Modal>
  );
}
